import React, { useState, useEffect, useRef } from 'react'
import { Card, CardTitle } from '../UI'
import { ANALYSIS_DONE_STATUSES, font, QUERY_READY_STATUSES } from './constants'
import { getAiSimulationDetails, createAiSimulation } from '../../requests'
import { isRTL, notifyError, notifySuccess } from '../../utils/helper'
import Empty from './Empty'
import { isSimulationLanguageEnglish } from './simulationTranslationStorage'
import useRole from '../../hooks/getRole'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

const STORAGE_KEY_PREFIX = 'mediplus_sim_selected_'
const STORAGE_KEY_CUSTOM = 'mediplus_sim_custom_queries_'
const STORAGE_KEY_DRAFT = 'mediplus_sim_custom_query_draft_'

function simStorageId(simulationId) {
  if (simulationId == null || simulationId === '') return ''
  return String(simulationId)
}

function loadCustomQueriesFromStorage(simulationId) {
  const sid = simStorageId(simulationId)
  if (!sid) return []
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_CUSTOM + sid)
    if (!raw) return []
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    return arr.filter((s) => typeof s === 'string' && s.trim() !== '')
  } catch {
    return []
  }
}

function saveCustomQueriesToStorage(simulationId, generatedList, fullQueriesList) {
  const sid = simStorageId(simulationId)
  if (!sid || !Array.isArray(generatedList) || !Array.isArray(fullQueriesList)) return
  try {
    const genSet = new Set(generatedList.map((q) => (typeof q === 'string' ? q : String(q))))
    const extras = fullQueriesList.filter((q) => {
      const t = typeof q === 'string' ? q : String(q)
      return t.trim() !== '' && !genSet.has(t)
    })
    sessionStorage.setItem(STORAGE_KEY_CUSTOM + sid, JSON.stringify(extras))
  } catch (_) { }
}

function loadCustomDraft(simulationId) {
  const sid = simStorageId(simulationId)
  if (!sid) return ''
  try {
    return sessionStorage.getItem(STORAGE_KEY_DRAFT + sid) ?? ''
  } catch {
    return ''
  }
}

function saveCustomDraft(simulationId, value) {
  const sid = simStorageId(simulationId)
  if (!sid) return
  try {
    const key = STORAGE_KEY_DRAFT + sid
    const s = value == null ? '' : String(value)
    if (s.trim() === '') {
      sessionStorage.removeItem(key)
    } else {
      sessionStorage.setItem(key, s)
    }
  } catch (_) { }
}

/** Lowercase, drop punctuation/symbols, collapse spaces — for matching user_queries to generated_queries. */
function normalizeQueryForMatch(value) {
  const s = typeof value === 'string' ? value : value?.text ?? String(value ?? '')
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function getDefaultSelectedIndices(queriesList, userQueries) {
  if (!Array.isArray(queriesList) || queriesList.length === 0) return new Set()
  const norm = (q) => (typeof q === 'string' ? q : q?.text ?? String(q))
  const list = queriesList.map(norm)
  const listKeys = list.map((q) => normalizeQueryForMatch(q))
  if (Array.isArray(userQueries) && userQueries.length > 0) {
    const indices = new Set()
    for (const uq of userQueries) {
      const text = typeof uq === 'string' ? uq : uq?.text ?? String(uq)
      const key = normalizeQueryForMatch(text)
      if (!key) continue
      const idx = listKeys.findIndex((k) => k === key)
      if (idx >= 0) indices.add(idx)
    }
    if (indices.size > 0) return indices
  }
  return new Set(list.map((_, i) => i))
}

function loadSelectedFromStorage(simulationId, queriesList) {
  const sid = simStorageId(simulationId)
  if (!sid) return null
  try {
    const key = STORAGE_KEY_PREFIX + sid
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const stored = JSON.parse(raw)
    if (!Array.isArray(stored) || stored.length === 0) return null
    const list = queriesList.map((q) => (typeof q === 'string' ? q : q?.text ?? String(q)))
    const indices = new Set()
    stored.forEach((s) => {
      const idx = list.indexOf(s)
      if (idx >= 0) indices.add(idx)
    })
    return indices.size > 0 ? indices : null
  } catch {
    return null
  }
}

function saveSelectedToStorage(simulationId, queriesList, selectedIndices) {
  const sid = simStorageId(simulationId)
  if (!sid || !queriesList?.length) return
  try {
    const key = STORAGE_KEY_PREFIX + sid
    const list = queriesList.map((q) => (typeof q === 'string' ? q : q?.text ?? String(q)))
    const selected = list.filter((_, i) => selectedIndices.has(i))
    sessionStorage.setItem(key, JSON.stringify(selected))
  } catch (_) { }
}

/** llm_platform is stored as a JSON-encoded array (e.g. '["claude","perplexity"]') when multiple
 * platforms are selected, or a bare string (e.g. "chatgpt") for a single platform. */
function countLlmPlatforms(raw) {
  if (raw == null) return 1
  if (Array.isArray(raw)) return raw.length || 1
  const value = String(raw).trim()
  if (!value) return 1
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.length || 1
  } catch {
    // not JSON — a single bare platform id/label
  }
  return 1
}

function QueryTab({ recentSimulationId = null, projectId = null, onRunSuccess, recentSimulationStatus = null, recentSimulationDetails = null, translateLanguage = null, translationViewLang = 'or' }) {
 
  const role = useRole()
  const userInfo = useSelector((state) => state.user)
  const context = useSelector((state) => state.context)
  const navigate = useNavigate()
  const [queries, setQueries] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(new Set())
  const [running, setRunning] = useState(false)
  const [customInput, setCustomInput] = useState('')

  const generatedQueriesRef = useRef([])
  const queriesOwnedBySimIdRef = useRef(null)
  const lastDraftSimIdRef = useRef(undefined)
  const translationViewLangRef = useRef(translationViewLang)
  const simulationLanguageRef = useRef('')

  const [simulationStatus, setSimulationStatus] = useState(null)
  const [translating, setTranslating] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const translationParam = new URLSearchParams(location.search).get('translate')
    const isTranslation = translationParam === 'true'

    translationViewLangRef.current = isTranslation || translationViewLang === 'en' ? 'en' : translationViewLang
  }, [translationViewLang, location.search])

  function mergeQueriesWithStoredCustoms(simulationId, generatedList) {
    generatedQueriesRef.current = generatedList
    const customs = loadCustomQueriesFromStorage(simulationId)
    const merged = [...generatedList]
    for (const c of customs) {
      if (!merged.includes(c)) merged.push(c)
    }
    return merged
  }

  useEffect(() => {
    const sid = simStorageId(recentSimulationId)

    if (simStorageId(lastDraftSimIdRef.current) !== sid) {
      lastDraftSimIdRef.current = sid || null
      setCustomInput(sid ? loadCustomDraft(sid) : '')
    }

    const translationParam = new URLSearchParams(location.search).get('translate')
    const isTranslation = translationParam === 'true'
    const showTranslation = isTranslation ? true : translationViewLang === 'en'

    const effectiveStatus = recentSimulationStatus ?? recentSimulationDetails?.status
    if (recentSimulationDetails && Number(effectiveStatus) >= 4) {
      const gen = recentSimulationDetails?.generated_queries ?? []
      const list = gen.map((q) => (typeof q === 'string' ? q : q?.text ?? String(q)))
      const merged = mergeQueriesWithStoredCustoms(sid, list)
      setQueries(merged)
      setSimulationStatus(recentSimulationDetails?.status ?? null)
      queriesOwnedBySimIdRef.current = sid
      simulationLanguageRef.current = String(recentSimulationDetails?.language ?? '')
      if (sid) {
        saveCustomQueriesToStorage(sid, list, merged)
      }
      const fromStorage = loadSelectedFromStorage(sid, merged)
      const nextSelected =
        merged.length === 0
          ? new Set()
          : fromStorage ??
          (gen.length === 0
            ? new Set(merged.map((_, i) => i))
            : getDefaultSelectedIndices(gen, recentSimulationDetails?.user_queries))
      setSelected(nextSelected)
      if (merged.length > 0 && sid) {
        saveSelectedToStorage(sid, merged, nextSelected)
      }
      setLoading(false)
      return
    }
    if (!sid) {
      setQueries([])
      setSelected(new Set())
      setSimulationStatus(null)
      setLoading(false)
      simulationLanguageRef.current = ''
      generatedQueriesRef.current = []
      queriesOwnedBySimIdRef.current = null
      return
    }
    setLoading(true)
    getAiSimulationDetails(recentSimulationId, showTranslation)
      .then((res) => {
        const data = res?.data
        const status = data?.status
        setSimulationStatus(status)
        simulationLanguageRef.current = String(data?.language ?? '')
        const gen = data?.generated_queries
        const list = Array.isArray(gen) ? gen.map((q) => (typeof q === 'string' ? q : q?.text ?? String(q))) : []
        const merged = mergeQueriesWithStoredCustoms(sid, list)
        setQueries(merged)
        queriesOwnedBySimIdRef.current = sid
        if (sid) {
          saveCustomQueriesToStorage(sid, list, merged)
        }
        if (merged.length === 0) {
          setSelected(new Set())
          return
        }
        const fromStorage = loadSelectedFromStorage(sid, merged)
        const nextSelected =
          fromStorage ??
          (gen.length === 0
            ? new Set(merged.map((_, i) => i))
            : getDefaultSelectedIndices(gen, data?.user_queries))
        setSelected(nextSelected)
        if (sid) {
          saveSelectedToStorage(sid, merged, nextSelected)
        }
      })
      .catch(() => {
        setQueries([])
        setSelected(new Set())
        setSimulationStatus(null)
        simulationLanguageRef.current = ''
        generatedQueriesRef.current = []
        queriesOwnedBySimIdRef.current = null
      })
      .finally(() => setLoading(false))
  }, [recentSimulationId, recentSimulationDetails, recentSimulationStatus, translationViewLang, location.search])

  useEffect(() => {
    const sid = simStorageId(recentSimulationId)
    if (!sid || simStorageId(queriesOwnedBySimIdRef.current) !== sid) return
    if (queries.length > 0) {
      saveSelectedToStorage(sid, queries, selected)
    }
  }, [recentSimulationId, queries, selected])

  const showingEnglish =
    translationViewLang === 'en' ||
    new URLSearchParams(location.search).get('translate') === 'true'

  const handleTranslateLanguage = async () => {
    if (!translateLanguage || translating) return
    setTranslating(true)
    try {
      const lang = showingEnglish ? 'or' : 'en'
      translationViewLangRef.current = lang
      const response = await translateLanguage(lang)
      if (!response) return
      const translate = lang === 'en' ? 'true' : 'false'
      const base = `/simulations/details/${recentSimulationId}/queries?simulation_id=${recentSimulationId}&project_id=${context?.project_id}&brand_id=${context?.brand_id}`
      if (role === 'super_admin') {
        navigate(`${base}&agency_id=${userInfo?.agencyId}&translate=${translate}`)
      } else {
        navigate(`${base}&translate=${translate}`)
      }
    } finally {
      setTranslating(false)
    }
  }

  const toggleOne = (i) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === queries.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(queries.map((_, i) => i)))
    }
  }

  const addCustom = () => {
    const text = customInput.trim()
    if (!text) return
    const sid = simStorageId(recentSimulationId)
    if (!sid) return
    setQueries((prev) => {
      const next = [...prev, text]
      setSelected((sel) => {
        const nextSel = new Set(sel)
        nextSel.add(prev.length)
        return nextSel
      })
      saveCustomQueriesToStorage(sid, generatedQueriesRef.current, next)
      return next
    })
    setCustomInput('')
    saveCustomDraft(sid, '')
  }

  const onCustomInputChange = (e) => {
    const v = e.target.value
    setCustomInput(v)
    const sid = simStorageId(recentSimulationId)
    if (sid) saveCustomDraft(sid, v)
  }

  const handleRunSimulation = async () => {

    if (!projectId) {
      notifyError('Project not found')
      return
    }
    const selectedQueries = queries.filter((_, i) => selected.has(i))
    if (selectedQueries.length === 0) {
      notifyError('Select at least one query to run')
      return
    }

    setRunning(true)
    try {
      const response = await createAiSimulation({ simulation_id: recentSimulationId, user_queries: selectedQueries })

      if (response?.status < 400) {
        notifySuccess('Simulation started successfully')
        onRunSuccess?.()
        if(role === 'super_admin') {
          navigate(`/simulations?project_id=${context?.project_id}?brand_id=${context?.brand_id}?agency_id=${userInfo?.agencyId}`)
        } else {
          navigate(`/simulations?project_id=${context?.project_id}?brand_id=${context?.brand_id}`)
        }
      }
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Failed to run simulation')
    } finally {
      setRunning(false)
    }
  }


  if (!recentSimulationId) {
    return (
      <Empty message="Failed simulation. Please create a new simulation." />
    )
  }


  if (simulationStatus == null) {
    return (
      <Empty message="AI is still analyzing the data. Please wait for a moment." type="progress" />
    )
  }



  if (simulationStatus === -1) {
    return (
      <Empty message="Failed simulation. Please create a new simulation again" />
    )
  }


  if (simulationStatus == 1 || simulationStatus == 2 || simulationStatus == 3 || loading) {
    return (
      <Empty message="Queries generation in progress..." type="progress" />
    )
  }

  if (QUERY_READY_STATUSES.includes(Number(simulationStatus))) {
    const simLanguage = recentSimulationDetails?.language ?? simulationLanguageRef.current ?? ''
    const isRTLanguageView =
      translationViewLangRef.current === 'en' ? false : isRTL(simLanguage)
    const rtlDir = isRTLanguageView ? 'rtl' : 'ltr'
    const rtlThin = isRTLanguageView ? { fontWeight: 300 } : {}

    return (
      <Card style={{ padding: '16px 22px' }}>
        <div dir={rtlDir} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {translateLanguage &&
            ANALYSIS_DONE_STATUSES.includes(Number(simulationStatus ?? recentSimulationStatus)) &&
            !isSimulationLanguageEnglish(simLanguage) && (
            <div
              style={{
                display: 'flex',
                justifyContent: isRTLanguageView ? 'flex-start' : 'flex-end',
              }}
            >
              <button
                type="button"
                onClick={handleTranslateLanguage}
                disabled={translating}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid rgba(0,0,0,0.12)',
                  background: 'rgba(0,0,0,0.04)',
                  color: '#363636',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: translating ? 'not-allowed' : 'pointer',
                  fontFamily: font,
                  opacity: translating ? 0.85 : 1,
                }}
              >
                {translating ? 'Translating…' : showingEnglish ? 'Show original' : 'Translate to English'}
              </button>
            </div>
          )}
          {/* Query list */}
          {queries.length > 0 && (
            <>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#363636',
                  fontFamily: font,
                  ...rtlThin,
                }}
              >
                <input
                  type="checkbox"
                  checked={queries.length > 0 && selected.size === queries.length}
                  onChange={toggleAll}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                Select all
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {queries.map((text, i) => (
                  <label
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: '10px 14px',
                      borderRadius: 8,
                      background: selected.has(i) ? 'rgba(239,68,68,0.08)' : 'rgba(0,0,0,0.03)',
                      border: `1px solid ${selected.has(i) ? 'rgba(239,68,68,0.3)' : 'rgba(0,0,0,0.08)'}`,
                      cursor: 'pointer',
                      fontFamily: font,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(i)}
                      onChange={() => toggleOne(i)}
                      style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
                    />
                    <span
                      dir="auto"
                      style={{ fontSize: 13, color: '#363636', flex: 1, textAlign: isRTLanguageView ? 'right' : 'left' }}
                    >
                      {text}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}

          {queries.length === 0 && !loading && (
            <p
              style={{
                fontSize: 13,
                color: '#999999',
                fontFamily: font,
                textAlign: isRTLanguageView ? 'right' : 'left',
                ...rtlThin,
              }}
            >
              No queries yet. Run a previous simulation to load queries or add a custom query below.
            </p>
          )}

          {/* Add Custom Query */}
          <Card style={{ padding: '16px 22px' }}>
            <CardTitle fontWeight={isRTLanguageView ? 300 : 600}>Add Custom Query</CardTitle>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <input
                dir={rtlDir}
                value={customInput}
                onChange={onCustomInputChange}
                placeholder="Type your custom query..."
                onKeyDown={(e) => e.key === 'Enter' && addCustom()}
                style={{
                  flex: 1,
                  background: 'rgba(0,0,0,0.04)',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: '#363636',
                  fontSize: 13,
                  fontFamily: font,
                  outline: 'none',
                  textAlign: isRTLanguageView ? 'right' : 'left',
                  ...rtlThin,
                }}
              />
              <button
                type="button"
                onClick={addCustom}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: font,
                }}
              >
                Add
              </button>
            </div>
          </Card>

          {/* Bottom: Run simulation */}
          <div style={{ paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <button
              type="button"
              onClick={handleRunSimulation}
              disabled={running || selected.size === 0}
              style={{
                width: '100%',
                padding: '16px 0',
                borderRadius: 10,
                border: 'none',
                background: (selected.size > 0 && !running)
                  ? 'linear-gradient(135deg,rgb(174, 207, 10), #C1D850)'
                  : 'rgba(0,0,0,0.08)',
                color: (selected.size > 0 && !running) ? '#fff' : '#999999',
                fontSize: 16,
                fontWeight: 600,
                cursor: (selected.size > 0 && !running) ? 'pointer' : 'not-allowed',
                fontFamily: font,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: selected.size > 0 && !running ? '0 4px 24px rgba(34,197,94,0.25)' : 'none',
              }}
            >

              {running ? <span style={{ fontSize: 14, color: '#fff', fontFamily: font }}>⚡ Processing...</span>
                : (() => {
                    const platformCount = countLlmPlatforms(recentSimulationDetails?.llm_platform)
                    return `⚡ Run Simulation — ${selected.size} queries × ${platformCount} platform${platformCount === 1 ? '' : 's'}`
                  })()}
            </button>
          </div>
        </div>
      </Card>
    )

  } else {
    return (
      <Empty message="Queries generation in progress..." type="progress" />
    )
  }


}

export default QueryTab
